import { Box, Chip, Stack, Typography, alpha, useTheme } from "@mui/material";

import type { DeploymentEvent } from "src/domains/application/hooks/useDeploymentEvents";

import { pulse } from "./job-detail-constants";
import {
  STATUS_CHIP_COLOR,
  groupDeployEventsByService,
} from "./job-detail-helpers";

export function ServiceDeployMatrix({
  events,
  templateServices,
}: {
  events: DeploymentEvent[];
  templateServices?: { name: string; type: string }[];
}) {
  const theme = useTheme();
  const services = groupDeployEventsByService(events, templateServices);
  if (services.length === 0) return null;

  return (
    <Box sx={{ mt: 1 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 0.5, display: "block", fontWeight: 600 }}
      >
        Service Deployments
      </Typography>
      <Stack spacing={0.5}>
        {services.map((svc) => {
          const statusColor =
            svc.status === "completed"
              ? theme.palette.success.main
              : svc.status === "in_progress"
                ? theme.palette.info.main
                : svc.status === "failed"
                  ? theme.palette.error.main
                  : theme.palette.grey[400];

          return (
            <Stack
              key={svc.name}
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                p: 1,
                border: `1px solid ${alpha(theme.palette.divider, 0.04)}`,
                borderRadius: 0.5,
                bgcolor:
                  svc.status === "in_progress"
                    ? alpha(theme.palette.info.main, 0.04)
                    : "transparent",
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: statusColor,
                  flexShrink: 0,
                  ...(svc.status === "in_progress" && {
                    animation: `${pulse} 1.5s infinite`,
                  }),
                }}
              />
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ minWidth: 60 }}
              >
                {svc.name}
              </Typography>
              <Chip
                label={svc.deployMethod}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: "0.7rem" }}
              />
              <Chip
                label={svc.status}
                size="small"
                color={STATUS_CHIP_COLOR[svc.status] || "default"}
                sx={{ height: 20, fontSize: "0.7rem" }}
              />
              {svc.url && (
                <Typography
                  variant="caption"
                  color="primary.main"
                  sx={{
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontFamily: "monospace",
                  }}
                >
                  {svc.url}
                </Typography>
              )}
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}
